import {
  AbsoluteFill,
  Audio,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { InstrumentIcon } from "../InstrumentIcon";
import { smooth, TAU } from "../orbit";
import { CLIMAX, PARTS, timeBeat } from "./score";
import {
  ORBITS,
  entryWeight,
  finalePulse,
  hit,
  iconPose,
  markerVisible,
  position,
  rotation,
} from "./motion";

export const COPY = {
  brand: "LOOP NIBBLE",
  title: "ORBIT",
  subtitle: "MUSIC BOX / II",
  track: "IN THE HALL OF THE",
  trackLast: "MOUNTAIN KING",
  composer: "EDVARD GRIEG",
};
const LABELS = ["MARIMBA", "BELL", "BASS", "PERCUSSION"];
export const OrbitV2Artwork: React.FC<{ time: number }> = ({ time }) => {
  const beat = timeBeat(time),
    final = finalePulse(time);
  const chase = smooth((beat - 32) / 16);
  const end = 1 - smooth((time - 17.65) / 0.35) * 0.25;
  return (
    <svg
      viewBox="0 0 1080 1920"
      width="100%"
      height="100%"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <radialGradient id="v2-bg">
          <stop stopColor="#17283F" />
          <stop offset="1" stopColor="#070B18" />
        </radialGradient>
        <radialGradient id="v2-core">
          <stop stopColor="#FFF1D8" stopOpacity=".8" />
          <stop offset=".2" stopColor="#B29AFF" stopOpacity=".23" />
          <stop offset="1" stopColor="#B29AFF" stopOpacity="0" />
        </radialGradient>
        {ORBITS.map((o) => (
          <radialGradient id={`v2-light-${o.id}`} key={o.id}>
            <stop stopColor={o.color} stopOpacity=".65" />
            <stop offset="1" stopColor={o.color} stopOpacity="0" />
          </radialGradient>
        ))}
      </defs>
      <rect width="1080" height="1920" fill="url(#v2-bg)" />
      <g opacity={end}>
        <text
          x="520"
          y="236"
          textAnchor="middle"
          fill="#8390A7"
          fontFamily="Arial, sans-serif"
          fontSize="21"
          letterSpacing="8"
        >
          {COPY.brand}
        </text>
        <text
          x="520"
          y="347"
          textAnchor="middle"
          fill="#F8F2E8"
          fontFamily="Arial, sans-serif"
          fontSize="90"
          fontWeight="700"
          letterSpacing="15"
        >
          {COPY.title}
        </text>
        <text
          x="520"
          y="394"
          textAnchor="middle"
          fill="#A3AEC2"
          fontFamily="Arial, sans-serif"
          fontSize="23"
          letterSpacing="9"
        >
          {COPY.subtitle}
        </text>
        <g transform="translate(520 900)">
          <circle r="450" fill="#0B1324" stroke="#202B40" strokeWidth="2" />
          <circle r="427" fill="none" stroke="#2B354A" strokeWidth="1" />
          {Array.from({ length: 64 }, (_, i) => {
            const a = (i * TAU) / 64;
            return (
              <line
                key={i}
                x1={Math.cos(a) * 430}
                y1={Math.sin(a) * 430}
                x2={Math.cos(a) * (i % 4 === 0 ? 441 : 435)}
                y2={Math.sin(a) * (i % 4 === 0 ? 441 : 435)}
                stroke="#687087"
                strokeOpacity={i % 4 === 0 ? 0.5 : 0.22}
                strokeWidth="2"
              />
            );
          })}
          <line
            x1="0"
            x2="0"
            y1="-419"
            y2="-75"
            stroke="#DFE9FC"
            strokeOpacity={0.13 + chase * 0.2 + final * 0.4}
            strokeWidth={2 + final * 3}
          />
          <path
            d="M-12,-452 L0,-436 L12,-452"
            fill="none"
            stroke="#FFF1D8"
            strokeWidth="4"
            strokeLinecap="round"
          />
          {ORBITS.map((o, index) => {
            const weight = entryWeight(o.id, time),
              events = PARTS[o.id];
            if (weight === 0) return null;
            const pulse = events.reduce(
              (m, e) => Math.max(m, hit(e, time, o.release * 0.45)),
              0,
            );
            const recent = events.filter(
              (e) =>
                time >= e.time &&
                time <
                  e.time +
                    (o.id === "bell" ? 0.85 : o.id === "bass" ? 0.55 : 0.28),
            );
            const r = o.radius;
            return (
              <g
                key={o.id}
                opacity={weight}
                transform={`scale(${0.82 + 0.18 * weight})`}
              >
                <circle
                  r={r}
                  fill="none"
                  stroke={o.color}
                  strokeWidth={18 + pulse * 12}
                  strokeOpacity={0.025 + pulse * 0.035}
                />
                <circle
                  r={r}
                  fill="none"
                  stroke={o.color}
                  strokeWidth={3 + pulse * (o.id === "bass" ? 5 : 2)}
                  strokeOpacity={0.44 + pulse * 0.45}
                />
                <g transform={`rotate(${rotation(time)})`}>
                  {Array.from({ length: 16 }, (_, i) => (
                    <circle
                      key={i}
                      cx={r * Math.cos((i * TAU) / 16)}
                      cy={r * Math.sin((i * TAU) / 16)}
                      r={i % 4 === 0 ? 3 : 1.5}
                      fill={o.color}
                      opacity=".4"
                    />
                  ))}
                  <path
                    d={`M0,${-r} A${r},${r} 0 0 1 ${r},0`}
                    fill="none"
                    stroke={o.color}
                    strokeWidth="9"
                    strokeOpacity=".13"
                    strokeLinecap="round"
                  />
                </g>
                {o.id === "bass" && (
                  <circle
                    r={r + 3 + 16 * pulse}
                    fill="none"
                    stroke={o.color}
                    strokeWidth={2 + 6 * pulse}
                    opacity={0.12 + pulse * 0.3}
                  />
                )}
                {events
                  .filter((e) => markerVisible(e, time))
                  .map((e) => {
                    const p = position(e, time, r),
                      delta = beat - e.startBeat;
                    const opacity =
                      e.finale && time >= CLIMAX
                        ? 0.85
                        : smooth((delta + 3.75) / 0.3) *
                          (1 - smooth(delta / 0.15));
                    const size =
                      o.id === "percussion" ? 7 : 10 + (e.midi % 12) * 0.28;
                    return (
                      <g key={e.id} opacity={opacity}>
                        {[0.022, 0.044, 0.066].map((delay, j) => {
                          const tail = position(e, time - delay, r);
                          return (
                            <circle
                              key={j}
                              cx={tail.x}
                              cy={tail.y}
                              r={size * (0.8 - j * 0.17)}
                              fill={o.color}
                              opacity={0.23 - j * 0.06}
                            />
                          );
                        })}
                        <circle
                          cx={p.x}
                          cy={p.y}
                          r={size + 10}
                          fill={o.color}
                          opacity=".09"
                        />
                        <circle cx={p.x} cy={p.y} r={size} fill={o.color} />
                        <circle
                          cx={p.x - 2}
                          cy={p.y - 3}
                          r={size * 0.33}
                          fill="#FFFDF7"
                          opacity=".8"
                        />
                      </g>
                    );
                  })}
                {recent.map((e) => {
                  const age = time - e.time;
                  const life =
                    o.id === "bell" ? 0.85 : o.id === "bass" ? 0.55 : 0.28;
                  const v = (1 - age / life) * e.velocity;
                  return (
                    <g key={e.id}>
                      <circle
                        cy={-r}
                        r={9 + age * (o.id === "bell" ? 115 : 140)}
                        fill="none"
                        stroke={o.color}
                        strokeWidth={o.id === "bass" ? 6 : 2.5}
                        opacity={v * 0.6}
                      />
                      {o.id === "marimba" &&
                        [-1, 1].map((sign) => (
                          <path
                            key={sign}
                            d={`M${sign * (15 + age * 65)},${-r - 8 - age * 44} l${sign * 6},-7`}
                            stroke={o.color}
                            strokeWidth="3"
                            opacity={v}
                          />
                        ))}
                      {o.id === "percussion" && (
                        <path
                          d={`M-16,${-r} h32 M0,${-r - 16} v32`}
                          stroke={o.color}
                          strokeWidth="3"
                          opacity={v}
                        />
                      )}
                    </g>
                  );
                })}
                <circle
                  cy={-r}
                  r={38 + pulse * (o.id === "bell" ? 66 : 40)}
                  fill={`url(#v2-light-${o.id})`}
                  opacity={0.16 + pulse * 0.84}
                />
                <circle cy={-r} r={7 + pulse * 7} fill={o.color} />
                <circle cy={-r} r={3 + pulse * 4} fill="#FFF5E4" />
                {time >= events[0].time &&
                  time < events[0].time + 0.7 &&
                  index > 0 && (
                    <circle
                      r={r + (time - events[0].time) * 65}
                      fill="none"
                      stroke={o.color}
                      strokeWidth="3"
                      opacity={(1 - (time - events[0].time) / 0.7) * 0.5}
                    />
                  )}
              </g>
            );
          })}
          {/* Phrase accents reward the octave changes, using the lead's actual attacks. */}
          {PARTS.marimba
            .filter((e) => e.startBeat === 16 || e.startBeat === 32)
            .map((e) => {
              const age = time - e.time;
              return age >= 0 && age < 0.9 ? (
                <circle
                  key={e.id}
                  r={45 + age * 420}
                  fill="none"
                  stroke={e.startBeat === 16 ? "#74EEFF" : "#FFB35C"}
                  strokeWidth="2"
                  opacity={(1 - age / 0.9) * e.velocity * 0.55}
                />
              ) : null;
            })}
          {/* Near-unison attacks build a comb on the shared axis before the final chord. */}
          {time >= CLIMAX &&
            [0, 0.12, 0.24].map((delay, i) => {
              const age = time - CLIMAX - delay;
              return age >= 0 && age < 1.25 ? (
                <circle
                  key={i}
                  r={30 + age * 340}
                  fill="none"
                  stroke={i === 1 ? "#74EEFF" : "#DAC8FF"}
                  strokeWidth={4 - i}
                  opacity={(1 - age / 1.25) * 0.55}
                />
              ) : null;
            })}
          <circle r={80 + final * 165} fill="url(#v2-core)" />
          <circle
            r={25 + final * 8}
            fill="#111C2F"
            stroke="#D6C6A1"
            strokeWidth={2 + final * 3}
          />
          <circle r={7 + final * 8} fill="#FFF0D1" />
        </g>
        <g transform="translate(520 1468)">
          {final > 0 && (
            <ellipse
              cy="-15"
              rx={400 + (1 - final) * 60}
              ry={80 + (1 - final) * 50}
              fill="url(#v2-core)"
              opacity={final * 0.85}
            />
          )}
          {ORBITS.map((o, i) => {
            const pose = iconPose(o.id, time),
              active = time >= PARTS[o.id][0].time;
            return (
              <g
                key={o.id}
                transform={`translate(${(i - 1.5) * 190} 0)`}
                color={o.color}
                opacity={active ? 1 : 0.16}
              >
                <circle
                  r={57 + pose.glow * 20}
                  fill={`url(#v2-light-${o.id})`}
                  opacity={0.2 + pose.glow * 0.8}
                />
                {pose.age < 0.6 && (
                  <circle
                    cy={-pose.jump * 0.3}
                    r={47 + pose.age * 110}
                    fill="none"
                    stroke={o.color}
                    strokeWidth={o.id === "bass" ? 5 : 2}
                    opacity={(1 - pose.age / 0.6) * pose.velocity * 0.6}
                  />
                )}
                <g
                  transform={`translate(0 ${-pose.jump}) rotate(${pose.rotate}) scale(${pose.scale})`}
                >
                  <circle
                    r="44"
                    fill="#101C30"
                    stroke={o.color}
                    strokeOpacity={0.22 + pose.glow * 0.7}
                    strokeWidth={1.5 + pose.glow * 2}
                  />
                  <circle
                    r="43"
                    fill={o.color}
                    opacity={0.035 + pose.glow * 0.16}
                  />
                  <InstrumentIcon instrument={o.id} />
                </g>
                <text
                  y="91"
                  textAnchor="middle"
                  fill={o.color}
                  fillOpacity=".78"
                  fontFamily="Arial, sans-serif"
                  fontSize="18"
                  letterSpacing="2"
                >
                  {LABELS[i]}
                </text>
              </g>
            );
          })}
        </g>
        <g fill="#A3AEC2" fontFamily="Arial, sans-serif" textAnchor="middle">
          <text x="520" y="1650" fontSize="20" letterSpacing="4">
            {COPY.track}
          </text>
          <text x="520" y="1686" fontSize="28" letterSpacing="5">
            {COPY.trackLast}
          </text>
          <text x="520" y="1730" fontSize="17" letterSpacing="4" opacity=".65">
            {COPY.composer}
          </text>
        </g>
      </g>
    </svg>
  );
};
export const OrbitMusicBoxV2: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  return (
    <AbsoluteFill style={{ backgroundColor: "#070B18" }}>
      <Audio src={staticFile("orbit-v2/music.wav")} />
      <OrbitV2Artwork time={frame / fps} />
    </AbsoluteFill>
  );
};
